import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Alert, 
  Paper, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Divider,
  CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SavedSearchIcon from '@mui/icons-material/SavedSearch';
import { authenticatedFetch } from '../utils/apiUtils';
import { getSavedFilters } from '../services/savedFiltersService';
import { getSavedFilterById, applySavedFilter } from '../utils/savedFiltersUtils';

/**
 * UploadFile component - handles CSV file uploads and initial data parsing
 */
const UploadFile = ({ onNext, setLoading, setFileData, setSelectedColumns, setFilterParams, setFilterConfig }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [fileStats, setFileStats] = useState(null);
  const [savedFilters, setSavedFilters] = useState([]);
  const [selectedFilterId, setSelectedFilterId] = useState('');
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Handle file selection
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    setError(null);
    setUploadSuccess(false);
    
    if (!selectedFile) {
      return;
    }
    
    if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
      setError('Please upload a CSV file.');
      return;
    }
    
    setFile(selectedFile);
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setError(null);
      setLoading(true);
      
      // Use authenticated fetch instead of axios
      const response = await authenticatedFetch('/api/upload', {
        method: 'POST',
        body: formData,
        // Don't include Content-Type header for FormData, browser will set it with boundary
        headers: {}
      });
      
      if (!response.ok) {
        // Handle error responses
        try {
          const errorData = await response.json();
          // Check if error is an object or string and handle accordingly
          if (typeof errorData.error === 'object') {
            throw new Error(JSON.stringify(errorData.error) || `Upload failed with status: ${response.status}`);
          } else {
            throw new Error(errorData.error || `Upload failed with status: ${response.status}`);
          }
        } catch (jsonError) {
          // If JSON parsing fails, use the status text
          throw new Error(`Upload failed: ${response.statusText || response.status}`);
        }
      }
      
      const responseData = await response.json();
      setLoading(false);
      setUploadSuccess(true);
      
      // Store file data in state for later use
      const { fileId, rowCount, columnCount, columns } = responseData;
      setFileStats({ rowCount, columnCount });
      // Use 'id' instead of 'fileId' to ensure consistent property naming throughout the application
      setFileData({ id: fileId, rowCount, columnCount, columns });
      
      // Store fileId in localStorage for the filter application process
      localStorage.setItem('currentFileId', fileId);
      console.log('Stored fileId in localStorage:', fileId);
    } catch (error) {
      setLoading(false);
      console.error('Upload error:', error);
      
      // Ensure we're displaying a string, not an object
      let errorMessage = 'An error occurred during upload';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'object') {
        // If error is an object, stringify it for display
        try {
          errorMessage = JSON.stringify(error);
        } catch (e) {
          errorMessage = 'Unknown error occurred';
        }
      }
      
      setError(errorMessage);
    }
  };

  // Load saved filters on component mount
  useEffect(() => {
    const loadFilters = async () => {
      try {
        setLoadingFilters(true);
        const response = await getSavedFilters();
        
        if (response.ok) {
          const data = await response.json();
          setSavedFilters(data.filters || []);
        } else {
          console.error('Failed to load saved filters');
        }
      } catch (error) {
        console.error('Error loading saved filters:', error);
      } finally {
        setLoadingFilters(false);
      }
    };
    
    loadFilters();
  }, []);

  // Handle moving to next step
  const handleNext = () => {
    if (!uploadSuccess) {
      setError('Please upload a file first.');
      return;
    }
    
    // Clear all previous state if continuing with manual selection
    // This prevents stale data from affecting the new workflow
    if (savedFilters.length > 0) {
      // Keep the fileData since we need it, but reset all other state
      setSelectedColumns([]);
      setFilterParams({
        model: 'o4-mini-high', // Always using o4-mini-high as the default model
        reasoningText: '',
        chunkSize: 300
      });
      setFilterConfig({});
      console.log('All previous filter data cleared for fresh start');
    }
    
    onNext();
  };
  
  // Apply a selected saved filter
  const handleApplySavedFilter = async () => {
    try {
      setError(null);
      setLoading(true);
      
      if (!selectedFilterId) {
        setError('Please select a filter to apply.');
        setLoading(false);
        return;
      }
      
      // Get the current file ID from the uploaded file
      const currentFileId = localStorage.getItem('currentFileId');
      
      if (!currentFileId) {
        setError('No file available. Please upload a file first.');
        setLoading(false);
        return;
      }
      
      console.log('Applying saved filter, fetching complete details:', selectedFilterId);
      
      // Get the full filter details including configuration and overrides
      const filterDetails = await getSavedFilterById(selectedFilterId);
      console.log('Retrieved complete filter details:', filterDetails);
      
      if (!filterDetails || !filterDetails.filter || !filterDetails.filter.filter_config) {
        throw new Error('Could not retrieve filter configuration');
      }
      
      const completeFilterConfig = filterDetails.filter.filter_config;
      const filterOverrides = filterDetails.filter.overrides || {};
      
      console.log('Filter configuration to apply:', completeFilterConfig);
      
      // IMPORTANT: Actually apply the saved filter via API call
      // This is the critical step that was missing!
      const result = await applySavedFilter(currentFileId, selectedFilterId);
      console.log('Saved filter applied successfully:', result);
      
      if (!result || !result.filterId) {
        throw new Error('Failed to apply saved filter');
      }
      
      // Store the original saved filter ID for later reference when saving
      // This is crucial for our approach to fetch original overrides when saving
      console.log('Storing original saved filter ID for later reference:', selectedFilterId);
      localStorage.setItem('savedFilterId', selectedFilterId);
      
      // Update local storage with the new filter ID
      localStorage.setItem('currentFilterId', result.filterId);
      
      // Store in currentFilterInUse to ensure continuous filter ID tracking for overrides
      console.log('FILTER-ID-CONTINUITY: Storing saved filter ID in localStorage:', result.filterId);
      localStorage.setItem('currentFilterInUse', result.filterId);
      
      // Handle saved filter with overrides
      if (filterOverrides && Object.keys(filterOverrides).length > 0) {
        console.log('Saved filter contains overrides:', filterOverrides);
        
        // No need to individually apply overrides - they were already sent in the applySavedFilter call
        // Just update localStorage for other components that rely on this information
        console.log('Storing filter ID with overrides in localStorage:', result.filterId);
        localStorage.setItem('currentFilterIdWithOverrides', result.filterId);
        localStorage.setItem('overridesApplied', 'true');
      }
      
      // Extract columns from the standardized structure
      const selectedColumns = [];
      
      // First priority: Get columns from the selectedColumns array
      if (completeFilterConfig.selectedColumns && Array.isArray(completeFilterConfig.selectedColumns) && 
          completeFilterConfig.selectedColumns.length > 0) {
        selectedColumns.push(...completeFilterConfig.selectedColumns);
        console.log('Extracted selectedColumns from array:', selectedColumns);
      }
      
      // Second priority: Get any columns from columnConfig if selectedColumns was empty
      if (selectedColumns.length === 0 && completeFilterConfig.columnConfig) {
        Object.keys(completeFilterConfig.columnConfig).forEach(column => {
          selectedColumns.push(column);
        });
        console.log('Extracted selectedColumns from columnConfig:', selectedColumns);
      }
      
      // Third priority: Look for column objects at the top level (old format)
      if (selectedColumns.length === 0) {
        Object.keys(completeFilterConfig).forEach(key => {
          if (typeof completeFilterConfig[key] === 'object' && 
              !Array.isArray(completeFilterConfig[key]) && 
              key !== 'columnConfig' && 
              key !== 'selectedColumns' && 
              !['model', 'reasoningText', 'temperature', 'chunkSize'].includes(key)) {
            selectedColumns.push(key);
          }
        });
        console.log('Extracted selectedColumns from top-level objects:', selectedColumns);
      }
      
      // Extract column-specific configurations
      let columnConfig = {};
      
      // First priority: Use the standardized columnConfig object
      if (completeFilterConfig.columnConfig && Object.keys(completeFilterConfig.columnConfig).length > 0) {
        columnConfig = completeFilterConfig.columnConfig;
        console.log('Using standardized columnConfig:', columnConfig);
      } 
      // Second priority: Extract from top-level objects (old format)
      else {
        Object.keys(completeFilterConfig).forEach(key => {
          if (typeof completeFilterConfig[key] === 'object' && 
              !Array.isArray(completeFilterConfig[key]) && 
              key !== 'columnConfig' && 
              key !== 'selectedColumns' && 
              !['model', 'reasoningText', 'temperature', 'chunkSize'].includes(key)) {
            columnConfig[key] = completeFilterConfig[key];
          }
        });
        console.log('Extracted columnConfig from top-level objects:', columnConfig);
      }
      
      // Ensure the columnConfig follows the standardized structure
      // Each column configuration should have at least an 'instructions' property
      const standardizedColumnConfig = {};
      
      Object.keys(columnConfig).forEach(column => {
        if (columnConfig[column].instructions !== undefined) {
          // Already has proper structure
          standardizedColumnConfig[column] = columnConfig[column];
        } else {
          // Convert to standardized structure with instructions property
          standardizedColumnConfig[column] = {
            instructions: typeof columnConfig[column].text === 'string' ? 
              columnConfig[column].text : 
              JSON.stringify(columnConfig[column]),
            ...columnConfig[column] // Keep other properties too
          };
        }
      });
      
      console.log('Standardized columnConfig structure:', standardizedColumnConfig);
      
      // Extract reasoning text with robust approach similar to columns
      let reasoningText = '';
      
      // First priority: Check direct reasoningText property in standardized format
      if (completeFilterConfig.reasoningText && typeof completeFilterConfig.reasoningText === 'string') {
        reasoningText = completeFilterConfig.reasoningText;
        console.log('Extracted reasoningText from standard property:', reasoningText.substring(0, 50) + '...');
      } 
      // Second priority: Check filterConfig.reasoningText (nested structure)
      else if (completeFilterConfig.filterConfig && completeFilterConfig.filterConfig.reasoningText) {
        reasoningText = completeFilterConfig.filterConfig.reasoningText;
        console.log('Extracted reasoningText from nested filterConfig:', reasoningText.substring(0, 50) + '...');
      }
      // Third priority: Check filter_config.reasoningText (API format)
      else if (completeFilterConfig.filter_config && completeFilterConfig.filter_config.reasoningText) {
        reasoningText = completeFilterConfig.filter_config.reasoningText;
        console.log('Extracted reasoningText from filter_config:', reasoningText.substring(0, 50) + '...');
      }
      
      if (!reasoningText) {
        console.warn('No reasoningText found in filter configuration');
      }
      
      // Store the filter configuration in localStorage to make it available in the filter configuration page
      localStorage.setItem('savedFilterConfig', JSON.stringify(completeFilterConfig));
      localStorage.setItem('savedFilterOverrides', JSON.stringify(filterOverrides));
      localStorage.setItem('savedFilterId', selectedFilterId);
      localStorage.setItem('applyingSavedFilter', 'true');
      
      // Also explicitly store the selected columns for better compatibility
      localStorage.setItem('selectedColumns', JSON.stringify(selectedColumns));
      
      // Apply the filter configuration to the app state
      
      // Set the selected columns if we found any
      if (selectedColumns.length > 0) {
        setSelectedColumns(selectedColumns);
        console.log('Set selected columns in app state:', selectedColumns);
      } else {
        console.warn('No selected columns found in filter configuration!');
      }
      
      // Set the filter parameters
      setFilterParams({
        model: completeFilterConfig.model || 'o4-mini-high',
        reasoningText: reasoningText, // Use the extracted reasoningText with multi-level search
        chunkSize: completeFilterConfig.chunkSize || 300
      });
      console.log('Set filter parameters with reasoningText:', reasoningText ? reasoningText.substring(0, 50) + '...' : 'None');
      
      // Set the column-specific configurations with standardized structure
      if (Object.keys(standardizedColumnConfig).length > 0) {
        setFilterConfig(standardizedColumnConfig);
        console.log('Set standardized column config in app state:', standardizedColumnConfig);
      } else {
        // Even if no columns were found, set an empty object to ensure consistent structure
        setFilterConfig({});
        console.warn('No column configuration found in filter!');
      }
      
      setLoading(false);
      
      // Skip to the configure filters step
      onNext();
      onNext();      
    } catch (error) {
      console.error('Error applying filter:', error);
      setError(error.message || 'An error occurred while applying the filter');
      setLoading(false);
    }
  };

  const autoUpload = async (droppedFile) => {
    if (!droppedFile) return;
    
    const formData = new FormData();
    formData.append('file', droppedFile);
    
    try {
      setError(null);
      setLoading(true);
      
      const response = await authenticatedFetch('/api/upload', {
        method: 'POST',
        body: formData,
        headers: {}
      });
      
      if (!response.ok) {
        try {
          const errorData = await response.json();
          if (typeof errorData.error === 'object') {
            throw new Error(JSON.stringify(errorData.error) || `Upload failed with status: ${response.status}`);
          } else {
            throw new Error(errorData.error || `Upload failed with status: ${response.status}`);
          }
        } catch (jsonError) {
          throw new Error(`Upload failed: ${response.statusText || response.status}`);
        }
      }
      
      const responseData = await response.json();
      setLoading(false);
      setUploadSuccess(true);
      
      // Store file data in state for later use
      const { fileId, rowCount, columnCount, columns } = responseData;
      setFileStats({ rowCount, columnCount });
      // Use 'id' instead of 'fileId' to ensure consistent property naming throughout the application
      setFileData({ id: fileId, rowCount, columnCount, columns });
      
      // Store fileId in localStorage for the filter application process
      localStorage.setItem('currentFileId', fileId);
      console.log('Stored fileId in localStorage from drag-and-drop:', fileId);
      
      // Set the file state so the UI updates correctly
      setFile(droppedFile);
    } catch (error) {
      setLoading(false);
      console.error('Upload error:', error);
      
      let errorMessage = 'An error occurred during upload';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'object') {
        try {
          errorMessage = JSON.stringify(error);
        } catch (e) {
          errorMessage = 'Unknown error occurred';
        }
      }
      
      setError(errorMessage);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header removed to avoid redundancy with page title */}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Box 
        component={Paper} 
        variant="outlined" 
        sx={{ 
          p: 6, 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#161c3e', // User-specified darker blue background
          border: `2px dashed ${dragActive ? 'rgba(67, 97, 238, 0.6)' : 'rgba(255, 255, 255, 0.1)'}`,
          borderRadius: 2,
          mb: 3,
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'rgba(67, 97, 238, 0.4)',
            backgroundColor: '#1a2046' // Slightly lighter hover state
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragActive(true);
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragActive(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragActive(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragActive(false);
          
          if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const droppedFile = e.dataTransfer.files[0];
            
            if (droppedFile.type !== 'text/csv' && !droppedFile.name.endsWith('.csv')) {
              setError('Please upload a CSV file.');
              return;
            }
            
            setFile(droppedFile);
            setError(null);
            setUploadSuccess(false);
            
            autoUpload(droppedFile);
          }
        }}
      >
        <input
          accept=".csv"
          style={{ display: 'none' }}
          id="upload-file"
          type="file"
          onChange={handleFileChange}
        />
        
        {!uploadSuccess ? (
          <>
            <CloudUploadIcon sx={{ fontSize: 64, color: dragActive ? 'primary.light' : 'primary.main', mb: 2, transition: 'color 0.2s ease' }} />
            <Typography variant="h6" gutterBottom>
              {file ? file.name : 'Drag & drop CSV file here'}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {file ? `${(file.size / 1024).toFixed(2)} KB` : 'Or click to browse files'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <label htmlFor="upload-file">
                <Button 
                  variant="outlined" 
                  component="span"
                  sx={{ 
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: 'text.secondary',
                    textTransform: 'none',
                    borderRadius: '4px'  
                  }}
                >
                  Browse Files
                </Button>
              </label>
              {file && (
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleUpload}
                  sx={{ 
                    textTransform: 'none', 
                    borderRadius: '4px',
                    fontWeight: 'medium',
                    px: 3
                  }}
                >
                  Upload
                </Button>
              )}
            </Box>
          </>
        ) : (
          <>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Upload Successful
            </Typography>
            {fileStats && (
              <Typography variant="body1" sx={{ mb: 2 }}>
                {fileStats.rowCount} rows and {fileStats.columnCount} columns detected
              </Typography>
            )}

            <Divider sx={{ width: '100%', my: 2 }} />

            {savedFilters.length > 0 && (
              <Box sx={{ width: '100%', mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  <SavedSearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Apply a Saved Filter
                </Typography>

                <FormControl sx={{ minWidth: 300, mb: 2 }}>
                  <InputLabel id="saved-filter-select-label">Select a filter</InputLabel>
                  <Select
                    labelId="saved-filter-select-label"
                    value={selectedFilterId}
                    onChange={(e) => setSelectedFilterId(e.target.value)}
                    label="Select a filter"
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {savedFilters.map((filter) => (
                      <MenuItem key={filter.id} value={filter.id}>
                        {filter.name} {filter.is_favorite && '⭐'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleApplySavedFilter}
                  disabled={!selectedFilterId}
                  startIcon={<SavedSearchIcon />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '4px',
                    fontWeight: 'medium',
                    px: 3,
                    mt: 1
                  }}
                >
                  Apply Selected Filter
                </Button>
              </Box>
            )}

            {loadingFilters && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                <Typography variant="body2">Loading saved filters...</Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Box /> {/* Empty box for spacing */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleNext}
          disabled={!uploadSuccess}
          sx={{
            textTransform: 'none',
            borderRadius: '4px',
            fontWeight: 'medium',
            px: 3
          }}
        >
          {savedFilters.length > 0 ? 'Continue with Manual Selection' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
};

export default UploadFile;
